// apps/web/src/pages/QuestionDetailPage.tsx
// 题目详情页 — 从搜索结果进入的只读查看页

import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getQuestionApi } from "@/api/questions";
import QuestionRenderer from "@components/practice/QuestionRenderer";

export default function QuestionDetailPage() {
  const navigate = useNavigate();
  const { bankId, qid } = useParams<{ bankId: string; qid: string }>();

  const { data: question, isLoading, isError, error } = useQuery({
    queryKey: ["question", bankId, qid],
    queryFn: () => getQuestionApi(bankId!, qid!),
    enabled: !!bankId && !!qid,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-surface">
        <Header onBack={() => navigate(-1)} />
        <div className="p-4 space-y-3">
          <div className="h-28 rounded-2xl bg-white shadow-card animate-pulse" />
          <div className="h-52 rounded-2xl bg-white shadow-card animate-pulse" />
        </div>
      </div>
    );
  }

  if (isError || !question) {
    return (
      <div className="min-h-screen bg-surface">
        <Header onBack={() => navigate(-1)} />
        <div className="px-8 py-20 text-center">
          <p className="text-sm text-danger">题目加载失败</p>
          <p className="mt-1 text-xs text-gray-400">{(error as Error)?.message ?? "请稍后重试"}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-8">
      <Header onBack={() => navigate(-1)} />

      <main className="px-4 pt-4 space-y-6">
        {question.chapter && (
          <div className="text-xs text-gray-400">{question.chapter}</div>
        )}

        <QuestionRenderer
          question={question}
          disabled
          showResult
          isCorrect
          correctAnswer={question.correct_answer}
          explanation={question.explanation}
          showUserAnswer={false}
        />

        <div className="grid grid-cols-2 gap-3">
          <Link
            to={`/banks/${bankId}`}
            className="py-3 rounded-xl bg-white text-center text-sm font-medium text-gray-700 shadow-card btn-tap"
          >
            返回题库
          </Link>
          <Link
            to={`/banks/${bankId}/questions/${qid}/edit`}
            className="py-3 rounded-xl bg-primary text-center text-sm font-medium text-white shadow-card btn-tap"
          >
            编辑题目
          </Link>
        </div>
      </main>
    </div>
  );
}

function Header({ onBack }: { onBack: () => void }) {
  return (
    <div className="sticky top-0 z-20 h-12 px-3 bg-white border-b border-gray-100 flex items-center justify-between">
      <button
        type="button"
        onClick={onBack}
        className="w-9 h-9 flex items-center justify-center text-gray-700 btn-tap"
        aria-label="返回"
      >
        <ChevronLeftIcon />
      </button>
      <h1 className="text-[16px] font-semibold text-gray-900">题目详情</h1>
      <span className="w-9 h-9" />
    </div>
  );
}

function ChevronLeftIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}
